import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { CheckCircle, EventBusy, Block } from '@mui/icons-material';
import axiosInstance from '../../../../utils/axiosConfig';

interface CoverageModalProps {
  open: boolean;
  date: string | null;
  onClose: () => void;
}

interface PersonnelItem {
  _id: string;
  name: string;
  rank?: string;
  status?: string;
  time?: string;
  notes?: string;
}

interface CoverageDetail {
  working: PersonnelItem[];
  onLeave: PersonnelItem[];
  unavailable: PersonnelItem[];
  notScheduled: PersonnelItem[];
  totalStaff: number;
  workingCount?: number;
  onLeaveCount?: number;
  unavailableCount?: number;
  notScheduledCount?: number;
}

const CoverageModal: React.FC<CoverageModalProps> = ({
  open,
  date,
  onClose
}) => {
  const [coverage, setCoverage] = useState<CoverageDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && date) {
      fetchCoverageDetail();
    }
  }, [open, date]);

  const fetchCoverageDetail = async () => {
    if (!date) return;

    try {
      setLoading(true);
      setError(null);
      const dateStr = new Date(date).toISOString().split('T')[0];
      const response = await axiosInstance.get(`/api/rota/coverage/${dateStr}`);
      console.log('🔍 Coverage API response:', response.data);
      console.log('🔍 Details object:', response.data?.details);
      
      // Use details object which has proper structure with _id, name, rank
      const details = response.data?.details || {
        working: [],
        onLeave: [],
        unavailable: []
      };
      
      // Map notScheduled strings to proper objects
      const notScheduled = (response.data?.notScheduled || []).map((name: string, index: number) => ({
        _id: `not-scheduled-${index}`,
        name,
        rank: 'Staff'
      }));
      
      setCoverage({
        ...details,
        notScheduled,
        totalStaff: response.data?.totalStaff || 0
      });
      
      setLoading(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch coverage details');
      setLoading(false);
    }
  };

  const renderPersonnelList = (
    personnel: PersonnelItem[],
    color: 'success' | 'warning' | 'default',
    icon: React.ReactNode
  ) => {
    if (personnel.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1, px: 2 }}>
          None
        </Typography>
      );
    }

    return (
      <List dense>
        {personnel.map((person) => (
          <ListItem key={person._id}>
            <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
              {icon}
            </Box>
            <ListItemText
              primary={person.name}
              secondary={person.time || person.rank || ''}
            />
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper'
        }
      }}
    >
      <DialogTitle>
        Coverage for {date ? new Date(date).toLocaleDateString('en-GB', { 
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }) : ''}
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : coverage ? (
          <Box>
            {/* Working */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Chip
                  icon={<CheckCircle />}
                  label={`Working (${coverage.working.length})`}
                  color="success"
                  size="small"
                />
              </Box>
              {renderPersonnelList(
                coverage.working,
                'success',
                <CheckCircle fontSize="small" color="success" />
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* On Leave */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Chip
                  icon={<EventBusy />}
                  label={`On Leave (${coverage.onLeave.length})`}
                  color="warning"
                  size="small"
                />
              </Box>
              {renderPersonnelList(
                coverage.onLeave,
                'warning',
                <EventBusy fontSize="small" color="warning" />
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Unavailable */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Chip
                  icon={<Block />}
                  label={`Unavailable (${coverage.unavailable.length})`}
                  color="default"
                  size="small"
                />
              </Box>
              {renderPersonnelList(
                coverage.unavailable,
                'default',
                <Block fontSize="small" color="disabled" />
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Not Yet Scheduled */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Chip
                  label={`Not Yet Scheduled (${coverage.notScheduled?.length || 0})`}
                  size="small"
                  sx={{ bgcolor: '#9E9E9E', color: 'white' }}
                />
              </Box>
              {coverage.notScheduled && coverage.notScheduled.length > 0 ? (
                <List dense>
                  {coverage.notScheduled.map((person) => (
                    <ListItem key={person._id}>
                      <ListItemText
                        primary={person.name}
                        secondary="No rota entry"
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1, px: 2 }}>
                  All staff scheduled
                </Typography>
              )}
            </Box>

            {/* Summary */}
            <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Total Staff: {coverage.totalStaff || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Scheduled: {(coverage.totalStaff || 0) - (coverage.notScheduled?.length || 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Not Yet Scheduled: {coverage.notScheduled?.length || 0}
              </Typography>
              
              {/* Show coverage status only if most staff are scheduled */}
              {coverage.notScheduled && coverage.notScheduled.length < (coverage.totalStaff || 0) * 0.5 ? (
                <Typography 
                  variant="body2" 
                  color={coverage.working.length >= 3 ? 'success.main' : coverage.working.length >= 2 ? 'warning.main' : 'error.main'}
                  fontWeight="medium"
                  sx={{ mt: 1 }}
                >
                  Coverage Status: {coverage.working.length >= 3 ? 'Good' : coverage.working.length >= 2 ? 'Adequate' : 'Low'}
                </Typography>
              ) : (
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  fontWeight="medium"
                  sx={{ mt: 1 }}
                >
                  Coverage Status: Too early to determine (most staff not yet scheduled)
                </Typography>
              )}
            </Box>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CoverageModal;